/* VoltEdge — hero-3d.js
   Soft 3D constellation: glowing particles on a slowly rotating
   sphere with perspective projection and mouse parallax.
   Vanilla JS, zero dependencies, pauses when off-screen,
   respects prefers-reduced-motion. */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('canvas[data-hero-3d]').forEach(init);

  function init(canvas) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var c1 = canvas.dataset.color1 || '#4e6af0';
    var c2 = canvas.dataset.color2 || '#9b6cf6';
    var c3 = canvas.dataset.color3 || '#37d2ea';
    var colors = [c1, c2, c3];

    var w = 0, h = 0, dpr = 1;
    var particles = [];
    var rotY = 0, rotX = -0.22, targetRX = -0.22, targetRY = 0;
    var FOV = 720;
    var running = true, visible = true, rafId = null;

    function hexToRgb(hex) {
      hex = hex.replace('#', '');
      if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
      var n = parseInt(hex, 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    var rgb = colors.map(hexToRgb);

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.max(1, w * dpr);
      canvas.height = Math.max(1, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    }

    function rand(a, b) { return a + Math.random() * (b - a); }

    function build() {
      particles = [];
      var count = w < 750 ? 110 : 210;
      var R = Math.min(w, h) * 0.55;
      for (var i = 0; i < count; i++) {
        var r = rand(0.6, 1) * R;
        var theta = rand(0, Math.PI * 2);
        var phi = Math.acos(rand(-1, 1));
        particles.push({
          x: r * Math.sin(phi) * Math.cos(theta),
          y: r * Math.cos(phi) * 0.7,
          z: r * Math.sin(phi) * Math.sin(theta),
          size: rand(1.1, 2.7),
          color: rgb[Math.floor(Math.random() * rgb.length)],
          tw: rand(0, Math.PI * 2)
        });
      }
    }

    var projected = [];
    function frame(now) {
      if (!running || !visible) return;
      rotY += 0.0015;
      rotX += (targetRX - rotX) * 0.04;
      var ry = rotY + targetRY * 0.001;

      var cy = Math.cos(ry), sy = Math.sin(ry);
      var cx = Math.cos(rotX), sx = Math.sin(rotX);
      var t = now * 0.001;

      ctx.clearRect(0, 0, w, h);
      projected.length = 0;

      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        var x = p.x * cy - p.z * sy;
        var z1 = p.x * sy + p.z * cy;
        var y = p.y * cx - z1 * sx;
        var z = p.y * sx + z1 * cx;

        var scale = FOV / (FOV + z + 420);
        projected.push({
          sx: w / 2 + x * scale,
          sy: h / 2 + y * scale,
          scale: scale,
          z: z,
          p: p
        });
      }

      /* connection lines between near, front-facing particles */
      ctx.lineWidth = 1;
      var maxDist = Math.min(w, h) * 0.16;
      for (var a = 0; a < projected.length; a++) {
        var A = projected[a];
        if (A.z > 120) continue;
        for (var b = a + 1; b < projected.length; b++) {
          var B = projected[b];
          if (B.z > 120) continue;
          var dx = A.sx - B.sx, dy = A.sy - B.sy;
          var dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDist) {
            var alpha = (1 - dist / maxDist) * 0.16 * A.scale;
            ctx.strokeStyle = 'rgba(' + A.p.color[0] + ',' + A.p.color[1] + ',' + A.p.color[2] + ',' + alpha.toFixed(3) + ')';
            ctx.beginPath();
            ctx.moveTo(A.sx, A.sy);
            ctx.lineTo(B.sx, B.sy);
            ctx.stroke();
          }
        }
      }

      /* particles */
      for (var j = 0; j < projected.length; j++) {
        var q = projected[j];
        var twinkle = 0.62 + 0.38 * Math.sin(t * 1.6 + q.p.tw);
        var alpha2 = Math.max(0.06, Math.min(0.85, q.scale * 0.9)) * twinkle;
        var rcol = q.p.color;
        var radius = q.p.size * q.scale;
        var grad = ctx.createRadialGradient(q.sx, q.sy, 0, q.sx, q.sy, radius * 3.2);
        grad.addColorStop(0, 'rgba(' + rcol[0] + ',' + rcol[1] + ',' + rcol[2] + ',' + alpha2.toFixed(3) + ')');
        grad.addColorStop(1, 'rgba(' + rcol[0] + ',' + rcol[1] + ',' + rcol[2] + ',0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(q.sx, q.sy, radius * 3.2, 0, Math.PI * 2);
        ctx.fill();
      }

      rafId = requestAnimationFrame(frame);
    }

    function start() {
      if (rafId) cancelAnimationFrame(rafId);
      if (reduced) {
        /* one static frame only */
        running = true; visible = true;
        rotY = 0.6;
        frameOnce();
        running = false;
        return;
      }
      running = true;
      rafId = requestAnimationFrame(frame);
    }

    function frameOnce() {
      var keep = running;
      running = true; visible = true;
      frame(performance.now());
      cancelAnimationFrame(rafId);
      running = keep;
    }

    /* mouse parallax */
    var host = canvas.closest('.hero') || canvas.parentElement;
    if (host && !reduced) {
      host.addEventListener('pointermove', function (e) {
        var rect = host.getBoundingClientRect();
        var mx = (e.clientX - rect.left) / rect.width - 0.5;
        var my = (e.clientY - rect.top) / rect.height - 0.5;
        targetRY = mx * 220;
        targetRX = -0.22 + my * 0.35;
      });
      host.addEventListener('pointerleave', function () {
        targetRY = 0; targetRX = -0.22;
      });
    }

    /* pause when off screen / tab hidden */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible && running && !reduced) {
          cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(frame);
        }
      }).observe(canvas);
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { cancelAnimationFrame(rafId); }
      else if (visible && running && !reduced) { rafId = requestAnimationFrame(frame); }
    });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { resize(); if (reduced) frameOnce(); }, 150);
    });

    resize();
    start();
  }
})();
